import React, { useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaFolder,
  FaBoxOpen,
  FaChartLine,
  FaChevronDown,
  FaChevronRight,
  FaDashcube,
  FaBuilding,
  FaShoppingCart,
  FaBox,
  FaBoxes,
} from "react-icons/fa";

const DEFAULT_MENU = [
  {
    id: "welcome",
    label: "Dashboard",
    icon: <FaDashcube />,
    path: "/dashboard",
  },
  {
    id: "company_details",
    label: "Company Master",
    icon: <FaBuilding />,
    path: "/dashboard/company-details",
  },
  {
    id: "all-masters",
    label: "Masters",
    icon: <FaBox />,
    children: [
      {
        id: "crm-master",
        label: "CRM Master",
        icon: <FaBoxOpen />,
        children: [
          {
            id: "blc-sub-ledger-group",
            label: "Ledger Group",
            path: "/dashboard/crm-master/submasters/ledger-group",
          },
          {
            id: "blc-sub-Currency",
            label: "Currency",
            path: "/dashboard/crm-master/submasters/currency",
          },
          {
            id: "blc-sub-ingo-term",
            label: "INGO Term",
            path: "/dashboard/crm-master/submasters/ingo-term",
          },
          {
            id: "blc-sub-payment-term",
            label: "Payment Term",
            path: "/dashboard/crm-master/submasters/payment-term",
          },
          {
            id: "blc-sub-client-to",
            label: "Client To",
            path: "/dashboard/crm-master/submasters/client-to",
          },
          {
            id: "blc-sub-agency",
            label: "Agency",
            path: "/dashboard/crm-master/submasters/agency",
          },
          {
            id: "blc-add-client",
            label: "Add Client",
            path: "/dashboard/crm-master/add-client",
          },
          {
            id: "blc-client-list",
            label: "Client List",
            path: "/dashboard/crm-master/client-list",
          },
        ],
      },
      {
        id: "product-master",
        label: "Product Master",
        icon: <FaBoxOpen />,
        children: [
          {
            id: "pm-category",
            label: "Category",
            path: "/dashboard/product/master/submasters/category",
          },
          {
            id: "pm-product-name",
            label: "Product Name",
            path: "/dashboard/product/master/submasters/product-name",
          },
          {
            id: "pm-collection",
            label: "Collection",
            path: "/dashboard/product/master/submasters/collection",
          },
          {
            id: "pm-materials",
            label: "Materials",
            path: "/dashboard/product/master/submasters/materials",
          },
          {
            id: "pm-hs-code",
            label: "HS Code",
            path: "/dashboard/product/master/submasters/hs-code",
          },
          {
            id: "pm-color",
            label: "Color",
            path: "/dashboard/product/master/submasters/color",
          },
          {
            id: "pm-finish",
            label: "Finish",
            path: "/dashboard/product/master/submasters/finish",
          },
          {
            id: "pm-suitable-for",
            label: "Suitable For",
            path: "/dashboard/product/master/submasters/suitable-for",
          },
          {
            id: "pm-pack-type",
            label: "Pack Type",
            path: "/dashboard/product/master/submasters/pack-type",
          },
        ],
      },
      {
        id: "general-master",
        label: "General Master",
        icon: <FaBoxOpen />,
        children: [
          {
            id: "blc-sub-barcode-type",
            label: "Barcode Type",
            path: "/dashboard/general-master/submasters/barcode-type",
          },
          {
            id: "gm-uom",
            label: "UOM",
            children: [
              {
                id: "gm-uom-weight",
                label: "Weight",
                path: "/dashboard/general-master/submasters/uom-weight",
              },
              {
                id: "gm-uom-count",
                label: "Count",
                path: "/dashboard/general-master/submasters/uom-count",
              },
              {
                id: "gm-uom-dimension",
                label: "Dimension",
                path: "/dashboard/general-master/submasters/uom-dimension",
              },
              {
                id: "gm-uom-volume",
                label: "Volume",
                path: "/dashboard/general-master/submasters/uom-volume",
              },
              {
                id: "gm-uom-loadability",
                label: "Loadability",
                path: "/dashboard/general-master/submasters/uom-loadability",
              },
            ],
          },
          {
            id: "gm-wrt-buyer",
            label: "Client Mapping",
            path: "/dashboard/general-master/wrt-buyer",
          },
        ],
      },
    ],
  },
  {
    id: "product",
    label: "Product",
    icon: <FaBoxes />,
    children: [
      {
        id: "pm-add-product",
        label: "Add Product",
        path: "/dashboard/product/add-product",
      },
      {
        id: "pm-catalog",
        label: "Catalogue",
        path: "/dashboard/product/catalog",
      },
      {
        id: "pm-client-catalog",
        label: "Client Catalogue",
        path: "/dashboard/product/client-catalog",
      },
      {
        id: "pm-cart",
        label: "View Cart",
        icon: <FaShoppingCart />,
        path: "/dashboard/product/cart",
      },
    ],
  },
  { id: "dms", label: "DMS", icon: <FaFolder />, path: "/dashboard/dms" },
  // {
  //   id: "sales",
  //   label: "Sales",
  //   icon: <FaChartLine />,
  //   path: "/dashboard/sales",
  // },
];

function useExpandedState() {
  const STORAGE_KEY = "leftMenuExpanded";
  const [expanded, setExpanded] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      return new Set(Array.isArray(arr) ? arr : []);
    } catch (e) {
      return new Set();
    }
  });

  const toggle = useCallback((id) => {
    setExpanded((prev) => {
      const copy = new Set(prev);
      if (copy.has(id)) copy.delete(id);
      else copy.add(id);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify([...copy]));
      } catch (e) {}
      return copy;
    });
  }, []);

  const isExpanded = useCallback((id) => expanded.has(id), [expanded]);

  const expandAllParents = useCallback((ids = []) => {
    setExpanded((prev) => {
      const copy = new Set(prev);
      ids.forEach((id) => copy.add(id));
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify([...copy]));
      } catch (e) {}
      return copy;
    });
  }, []);

  return { toggle, isExpanded, expandAllParents };
}

const LeftMenuItems = ({
  menu = DEFAULT_MENU,
  onSelect,
  collapsed = false,
}) => {
  const location = useLocation();
  const { toggle, isExpanded, expandAllParents } = useExpandedState();

  React.useEffect(() => {
    const activeIdPath = findActivePath(menu, location.pathname);
    if (activeIdPath) expandAllParents(activeIdPath.slice(0, -1));
  }, [location.pathname, expandAllParents, menu]);

  return (
    <nav className="left-menu">
      <ul className="list-unstyled pe-0 overflow-x-hidden mb-0">
        {menu.map((item) => (
          <MenuItem
            key={item.id}
            item={item}
            depth={0}
            toggle={toggle}
            isExpanded={isExpanded}
            onSelect={onSelect}
            locationPath={location.pathname}
            collapsed={collapsed}
          />
        ))}
      </ul>
    </nav>
  );
};

function findActivePath(menu, pathname) {
  const path = [];
  const found = (items, parents = []) => {
    for (const it of items) {
      const currentParents = [...parents, it.id];
      if (it.path && it.path === pathname) {
        path.push(...currentParents);
        return true;
      }
      if (it.id === pathname || `/${it.id}` === pathname) {
        path.push(...currentParents);
        return true;
      }
      if (it.children) {
        if (found(it.children, currentParents)) return true;
      }
    }
    return false;
  };
  found(menu);
  return path.length ? path : null;
}

const MenuItem = ({
  item,
  depth,
  toggle,
  isExpanded,
  onSelect,
  locationPath,
  collapsed,
}) => {
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const expanded = isExpanded(item.id);

  const isActive = item.path
    ? item.path === locationPath
    : locationPath === `/${item.id}` || locationPath === item.id;

  const paddingLeft = collapsed ? 0 : 12 + depth * 12;

  if (collapsed) {
    if (!item.icon) return null;
    return (
      <li>
        <div
          className={`menu-item-collapsed d-flex align-items-center justify-content-center rounded-2 my-1 ${isActive ? "active-menu text-white" : "text-dark"}`}
          title={item.label}
          onClick={() => {
            if (hasChildren) toggle(item.id);
            else if (item.path && onSelect) onSelect(item);
          }}
        >
          {item.path ? (
            <Link
              to={item.path}
              className={`d-flex align-items-center justify-content-center w-100 h-100 text-decoration-none ${isActive ? "text-white" : "text-dark"}`}
            >
              {item.icon}
            </Link>
          ) : (
            <span className={isActive ? "text-white" : "text-secondary"}>
              {item.icon}
            </span>
          )}
        </div>
      </li>
    );
  }

  return (
    <li>
      <div
        className={`menu-item d-flex align-items-center justify-content-between rounded-2 my-1 p-2 ${isActive ? "active-menu text-white" : "text-dark"}`}
        style={{ paddingLeft }}
        onClick={() => {
          if (hasChildren) toggle(item.id);
          else if (onSelect) onSelect(item);
        }}
      >
        <div className="w-100 h-100 d-flex align-items-center">
          {item.icon && <span className="me-2 flex-shrink-0">{item.icon}</span>}
          {item.path ? (
            <Link
              to={item.path}
              className={`d-block w-100 text-decoration-none menu-item-label ${isActive ? "text-white" : "text-dark"} ${expanded ? "fw-bold" : ""}`}
            >
              {item.label}
              {item?.id === "blc-add-client" && (
                <span className="badge bg-secondary ms-2">create</span>
              )}
              {item?.id === "pm-add-product" && (
                <span className="badge bg-secondary ms-2">create</span>
              )}
              {item?.id === "blc-client-list" && (
                <span className="badge bg-secondary ms-2">view</span>
              )}
              {item?.id === "pm-catalog" && (
                <span className="badge bg-secondary ms-2">view</span>
              )}
            </Link>
          ) : (
            <span
              className={`menu-item-label ${isActive ? "text-white" : ""} ${expanded ? "fw-bold" : ""}`}
            >
              {item.label}
            </span>
          )}
        </div>

        {hasChildren && (
          <button
            className="btn btn-sm btn-light ms-2 flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              toggle(item.id);
            }}
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? (
              <FaChevronDown size={10} />
            ) : (
              <FaChevronRight size={10} />
            )}
          </button>
        )}
      </div>

      {hasChildren && expanded && (
        <ul className="list-unstyled ms-2 pe-0 overflow-x-hidden mb-0">
          {item.children.map((child) => (
            <MenuItem
              key={child.id}
              item={child}
              depth={depth + 1}
              toggle={toggle}
              isExpanded={isExpanded}
              onSelect={onSelect}
              locationPath={locationPath}
              collapsed={false}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

export default LeftMenuItems;
